import React, { useState } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import LayoutWrapper from '../components/LayoutWrapper';
import ClassCard from '../components/ClassCard';
import ClassScheduleExpanded from '../components/ClassScheduleExpanded';
import { useClassData } from '../hooks/useClassData';
import { ClassBookingService } from '../Services/classBookingService.ts';
import { calculateCycleDates } from '../utils/classUtils';

const ClassList = () => {
    const { classes, loading, refetchClasses } = useClassData();

    // State management
    const [expandedClasses, setExpandedClasses] = useState(new Set());
    const [bulkSelectedDates, setBulkSelectedDates] = useState([]);

    // Services
    const bookingService = new ClassBookingService();

    // Helper functions
    const getClassSelectedDates = (classId) => {
        return bulkSelectedDates.filter(key => key.startsWith(`${classId}-`));
    };

    const toggleClassExpansion = (classId) => {
        const newExpanded = new Set(expandedClasses);
        if (newExpanded.has(classId)) {
            newExpanded.delete(classId);
        } else {
            newExpanded.add(classId);
        }
        setExpandedClasses(newExpanded);
    };

    const handleBulkDateToggle = (classId, date) => {
        const key = `${classId}-${date.toISOString()}`;
        if (bulkSelectedDates.includes(key)) {
            setBulkSelectedDates(bulkSelectedDates.filter(d => d !== key));
        } else {
            setBulkSelectedDates([...bulkSelectedDates, key]);
        }
    };

    const handleClassBulkBooking = async (classId) => {
        const classSelectedDates = bulkSelectedDates.filter(key => key.startsWith(`${classId}-`));
        if (classSelectedDates.length === 0) return;
        
        const dates = classSelectedDates.map(key => key.substring(`${classId}-`.length));

        const result = await bookingService.bookBulkDates(classId, dates);

        if (result.success) {
            if (result.paymentUrl) {
                window.location.href = result.paymentUrl;
            } else {
                alert('Classes booked successfully!');
                window.location.reload();
            }
        } else {
            alert(result.error);
        }
    };

    const handleClearSelection = (classId) => {
        setBulkSelectedDates(bulkSelectedDates.filter(key => !key.startsWith(`${classId}-`)));
    };

    const handleBook = async (venueClass) => {
        // For one-time classes, use direct booking
        const result = await bookingService.bookSingleClass(venueClass.id);

        if (result.success) {
            await refetchClasses();

            if (result.paymentUrl) {
                window.location.href = result.paymentUrl;
            } else {
                alert('Class booked successfully!');
            }
        } else {
            alert(result.error);
        }
    };

    if (loading) {
        return (
            <LayoutWrapper title="Available Classes">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper 
            title="Available Classes"
            subtitle="Browse and book classes at your venue"
        >
            {classes.length === 0 ? (
                <div className="text-center py-12">
                    <CalendarIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No classes available at this time.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Class Cards */}
                    {classes.map(venueClass => {
                        const allDates = venueClass.cycleType !== undefined
                            ? calculateCycleDates(venueClass.startDate, venueClass.cycleType, 6, venueClass.expirationDate)
                            : [new Date(venueClass.startDate)];

                        return (
                            <ClassCard
                                key={venueClass.id}
                                venueClass={venueClass}
                                isExpanded={expandedClasses.has(venueClass.id)}
                                onToggleExpansion={toggleClassExpansion}
                                onBookClass={handleBook}
                                isPublic={false}
                                showUserStatus={true}
                            >
                                {/* Expanded Schedule View */}
                                {venueClass.cycleType !== undefined && (
                                    <ClassScheduleExpanded
                                        venueClass={venueClass}
                                        allDates={allDates}
                                        bulkSelectedDates={bulkSelectedDates}
                                        onDateToggle={handleBulkDateToggle}
                                        onClearSelection={handleClearSelection}
                                        onBulkBooking={handleClassBulkBooking}
                                        getClassSelectedDates={getClassSelectedDates}
                                        showUserBookings={true}
                                    />
                                )}
                            </ClassCard>
                        );
                    })}
                </div>
            )}
        </LayoutWrapper>
    );
};

export default ClassList;
